import type { KeyboardEventHandler, MutableRefObject } from 'react';
import styled from 'styled-components';
import { ArrowMore } from '@elfsight/icons';
import { getColorWithAlpha } from '@elfsight/lib-utils';
import { ItemIcon } from '@elfsight/widget-modal';
import { IconWithOverlay } from './IconWithOverlay';

interface FlagDropdownTriggerProps {
  countryIconSrc: string;
  ariaLabel: string;
  flagContainerRef: MutableRefObject<HTMLButtonElement | null>;
  open: boolean;
  isHidden: boolean;
  fieldColor: string;
  onClick: () => void;
  onFocus: () => void;
  onBlur: () => void;
  onKeyDown?: KeyboardEventHandler;
}

export const FlagDropdownTrigger = ({
  onClick,
  ariaLabel,
  countryIconSrc,
  flagContainerRef,
  open,
  onFocus,
  onBlur,
  onKeyDown,
  fieldColor,
  isHidden
}: FlagDropdownTriggerProps) => {
  return (
    <Container
      ref={flagContainerRef}
      _isHidden={isHidden}
      _baseColor={fieldColor}
      _isOpen={open}
      aria-label={ariaLabel}
      type="button"
      aria-haspopup="listbox"
      aria-expanded={open}
      onClick={onClick}
      onFocus={onFocus}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
    >
      <IconWithOverlay>
        <FlagIcon src={countryIconSrc} size={[24, 18]} />
      </IconWithOverlay>
      <RotatingIcon
        _fillColor={fieldColor}
        _isOpen={open}
        component={ArrowMore}
        size={20}
      />
    </Container>
  );
};

const Container = styled.button<{
  _baseColor: string;
  _isOpen: boolean;
  _isHidden: boolean;
}>`
  align-items: center;
  display: flex;
  width: 48px;
  height: 24px;
  padding: 2px 1px 2px 3px;
  border-radius: 5px;
  margin: 0;
  border: none;
  background-color: ${({ _isOpen, _baseColor }) =>
    _isOpen ? getColorWithAlpha(_baseColor, 0.1) : 'transparent'};
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.44, 0.2, 0.2, 1);

  &:hover {
    background-color: ${({ _isOpen, _baseColor }) =>
      getColorWithAlpha(_baseColor, _isOpen ? 0.1 : 0.05)};
  }

  &:active,
  &:focus {
    background-color: ${({ _baseColor }) => getColorWithAlpha(_baseColor, 0.1)};
  }

  ${({ _isHidden }) =>
    _isHidden &&
    `
      opacity: 0;
      width: 0;
      padding: 0;
      margin: 0;
      transform: scaleX(0) translateX(-10px);
      pointer-events: none;
    `}
`;

const RotatingIcon = styled(ItemIcon)<{ _isOpen: boolean; _fillColor: string }>`
  transform: ${({ _isOpen }) => (_isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
  fill: ${({ _fillColor }) => _fillColor};
  transition: transform 0.15s ease-in-out;
`;

const FlagIcon = styled(ItemIcon)`
  & svg {
    border-radius: 2px;
    outline: 1px solid rgba(118, 118, 118, 0.2) !important;
    outline-offset: -1px;
  }
`;
